/**
 * MOCK DATA — NO BACKEND YET
 *
 * Catalog hierarchy modeled on IGUZZINI (real manufacturer reference data
 * supplied by the product team for the analytics demo). Analytics fields
 * are synthetically generated per-variant via a seeded RNG keyed off the
 * SKU, so values stay stable across reloads.
 *
 * When the backend is implemented this file becomes the data contract:
 * every analytics field below must be computable from real TAMAN events
 * (matches, applies, favorites, inquiries).
 *
 * Series images are referenced by the SERIES_IMAGE mapping below using
 * /lamps/images/*.jpg paths. Add new image files there and update the
 * mapping for new series; if an image is missing the UI falls back to a
 * coloured initial chip.
 */
import {
  LuminaireAnalytics,
  MockLuminaire,
  MonthlyAnalyticsPoint,
  PROJECT_TYPES,
  ProjectType,
} from '../types/analytics';

// ─────────────────────────────────────────────────────────────────────────────
// Seeded RNG (mulberry32) — deterministic per-SKU.
// ─────────────────────────────────────────────────────────────────────────────

const hashString = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (seed: number) => {
  let t = seed;
  return () => {
    t = (t + 0x6d2b79f5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const between = (rng: () => number, min: number, max: number) => min + rng() * (max - min);
const intBetween = (rng: () => number, min: number, max: number) => Math.floor(between(rng, min, max + 1));
const pickN = <T>(rng: () => number, arr: readonly T[], n: number): T[] => {
  const copy = [...arr];
  const out: T[] = [];
  for (let i = 0; i < n && copy.length; i++) {
    const idx = Math.floor(rng() * copy.length);
    out.push(copy.splice(idx, 1)[0]);
  }
  return out;
};

// ─────────────────────────────────────────────────────────────────────────────
// Segment profiles control the realistic shape of each luminaire's analytics.
// ─────────────────────────────────────────────────────────────────────────────

type Segment = MockLuminaire['segment'];

interface SegmentProfile {
  compatibility: [number, number];
  optimalGap: [number, number];
  matchesPerMonth: [number, number];
  applyRate: [number, number];
  favoriteRate: [number, number];
  projectTypeCount: [number, number];
  regions: [number, number];
  leadConversion: [number, number];
}

const SEGMENT_PROFILES: Record<Segment, SegmentProfile> = {
  flagship: {
    compatibility: [80, 95],
    optimalGap: [3, 10],
    matchesPerMonth: [180, 950],
    applyRate: [32, 48],
    favoriteRate: [10, 22],
    projectTypeCount: [6, 10],
    regions: [8, 18],
    leadConversion: [18, 32],
  },
  workhorse: {
    compatibility: [60, 80],
    optimalGap: [10, 22],
    matchesPerMonth: [50, 220],
    applyRate: [18, 32],
    favoriteRate: [4, 12],
    projectTypeCount: [3, 7],
    regions: [3, 10],
    leadConversion: [8, 18],
  },
  niche: {
    compatibility: [70, 92],
    optimalGap: [5, 14],
    matchesPerMonth: [12, 60],
    applyRate: [22, 40],
    favoriteRate: [6, 14],
    projectTypeCount: [1, 3],
    regions: [1, 6],
    leadConversion: [10, 22],
  },
  underperformer: {
    compatibility: [25, 55],
    optimalGap: [25, 55],
    matchesPerMonth: [0, 4],
    applyRate: [0, 6],
    favoriteRate: [0, 2],
    projectTypeCount: [0, 2],
    regions: [0, 2],
    leadConversion: [0, 3],
  },
  new: {
    compatibility: [55, 80],
    optimalGap: [12, 28],
    matchesPerMonth: [2, 20],
    applyRate: [10, 30],
    favoriteRate: [2, 8],
    projectTypeCount: [1, 4],
    regions: [1, 4],
    leadConversion: [4, 14],
  },
};

const REGION_CODES = [
  'IT',
  'DE',
  'FR',
  'ES',
  'NL',
  'BE',
  'AT',
  'CH',
  'PL',
  'HR',
  'SI',
  'CZ',
  'SK',
  'HU',
  'PT',
  'GB',
  'IE',
  'SE',
  'NO',
  'DK',
  'FI',
];

const SERIES_THUMB_COLOR: Record<string, string> = {
  lavinia: '#4f46e5',
  'platea-pro': '#0ea5e9',
  street: '#10b981',
  billboard: '#f59e0b',
};

// Real product photos supplied by the product team, keyed by series.
// Series without a photo fall back to the coloured initials chip in LampThumb.
const SERIES_IMAGE: Record<string, string> = {
  lavinia: '/lamps/images/lavinia-pole-mounted.jpg',
  'platea-pro': '/lamps/images/platea-pro-pole-mounted.jpg',
  street: '/lamps/images/street-pole-top.jpg',
};

// IES photometric files supplied per SKU (LM-63). Only a subset of the catalog
// has a file; SKUs without one fall back to a "no photometric data" state in
// the 3D scene preview. Files live in /public/lamps/ies/.
const IES_FILE: Record<string, string> = {
  '2137': '2137_0.ies',
  '2138': '2138_1.ies',
  '2139': '2139_2.ies',
  EH23: 'EH23_1.ies',
  EH27: 'EH27_0.ies',
  EQ60: 'EQ60_2.ies',
  P882: 'P882_2.ies',
  P883: 'P883_0.ies',
  P891: 'P891_1.ies',
  UH47: 'UH47_0.ies',
  UH48: 'UH48_1.ies',
};

// ─────────────────────────────────────────────────────────────────────────────
// 12-month synthetic time series.
// ─────────────────────────────────────────────────────────────────────────────

const buildTimeSeries = (
  rng: () => number,
  segment: Segment,
  endingValues: { compatibility: number; matches: number; applies: number; favorites: number; inquiries: number },
): MonthlyAnalyticsPoint[] => {
  const now = new Date();
  const out: MonthlyAnalyticsPoint[] = [];
  const sparseStart = segment === 'new' ? 12 - intBetween(rng, 2, 4) : 0;

  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthIdx = 11 - i;
    const blank = monthIdx < sparseStart;
    const seasonal = 1 + 0.15 * Math.sin((monthIdx / 12) * Math.PI * 2);
    const noise = 0.85 + rng() * 0.3;
    const scale = blank ? 0 : seasonal * noise;

    out.push({
      month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      compatibilityScoreAvg: blank ? 0 : Math.max(0, Math.min(100, endingValues.compatibility * (0.9 + rng() * 0.2))),
      matchFrequencyTotal: Math.round(endingValues.matches * scale),
      applyCount: Math.round(endingValues.applies * scale),
      favoriteCount: Math.round(endingValues.favorites * scale),
      inquiriesGenerated: Math.round(endingValues.inquiries * scale),
    });
  }
  return out;
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-variant analytics generator.
// ─────────────────────────────────────────────────────────────────────────────

const buildAnalytics = (sku: string, segment: Segment): LuminaireAnalytics => {
  const rng = mulberry32(hashString(sku));
  const p = SEGMENT_PROFILES[segment];

  const compatibilityScoreAvg = between(rng, ...p.compatibility);
  const compatibilityScoreP90 = Math.min(100, compatibilityScoreAvg + between(rng, 3, 9));
  const optimalGapPercent = between(rng, ...p.optimalGap);
  const matchFrequencyPerMonth = between(rng, ...p.matchesPerMonth);
  const matchFrequencyTotal = Math.round(matchFrequencyPerMonth * between(rng, 9, 12));
  const top100Multiplier = between(rng, 2.5, 4.5);
  const top100MatchCount = Math.round(matchFrequencyTotal * top100Multiplier);
  const top3Share = between(rng, 0.04, 0.12);
  const top3MatchCount = Math.round(top100MatchCount * top3Share);
  const applyRatePercent = between(rng, ...p.applyRate);
  const applyCount = Math.round((matchFrequencyTotal * applyRatePercent) / 100);
  const favoriteRatePercent = between(rng, ...p.favoriteRate);
  const favoriteCount = Math.round((matchFrequencyTotal * favoriteRatePercent) / 100);

  const projectTypeCount = intBetween(rng, ...p.projectTypeCount);
  const projectTypesCovered = pickN<ProjectType>(rng, PROJECT_TYPES, projectTypeCount);
  const regionsCount = intBetween(rng, ...p.regions);
  const regionsActive = pickN(rng, REGION_CODES, regionsCount);

  const leadConversionRatePercent = between(rng, ...p.leadConversion);
  const inquiriesGenerated = Math.round((matchFrequencyTotal * leadConversionRatePercent) / 100);

  const monthlyTimeSeries = buildTimeSeries(rng, segment, {
    compatibility: compatibilityScoreAvg,
    matches: matchFrequencyPerMonth,
    applies: applyCount / 12,
    favorites: favoriteCount / 12,
    inquiries: inquiriesGenerated / 12,
  });

  const now = new Date();
  const daysAgoMatched = segment === 'underperformer' ? null : intBetween(rng, 0, segment === 'new' ? 30 : 90);
  const daysAgoFirstSeen = segment === 'new' ? intBetween(rng, 30, 120) : intBetween(rng, 200, 1400);

  return {
    compatibilityScoreAvg,
    compatibilityScoreP90,
    optimalGapPercent,
    matchFrequencyTotal: segment === 'underperformer' ? 0 : matchFrequencyTotal,
    matchFrequencyPerMonth: segment === 'underperformer' ? 0 : matchFrequencyPerMonth,
    top3MatchCount: segment === 'underperformer' ? 0 : top3MatchCount,
    top100MatchCount: segment === 'underperformer' ? 0 : top100MatchCount,
    applyCount: segment === 'underperformer' ? 0 : applyCount,
    applyRatePercent: segment === 'underperformer' ? 0 : applyRatePercent,
    favoriteCount: segment === 'underperformer' ? 0 : favoriteCount,
    favoriteRatePercent: segment === 'underperformer' ? 0 : favoriteRatePercent,
    projectTypesCovered,
    projectTypeCount,
    regionsActive,
    regionsCount,
    inquiriesGenerated: segment === 'underperformer' ? 0 : inquiriesGenerated,
    leadConversionRatePercent: segment === 'underperformer' ? 0 : leadConversionRatePercent,
    monthlyTimeSeries,
    lastMatchedAt: daysAgoMatched === null ? null : new Date(now.getTime() - daysAgoMatched * 86400000).toISOString(),
    firstSeenAt: new Date(now.getTime() - daysAgoFirstSeen * 86400000).toISOString(),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// IGUZZINI catalog — series → products → variants, sourced from product team.
// ─────────────────────────────────────────────────────────────────────────────

interface SeriesDef {
  id: string;
  name: string;
}

export const MOCK_SERIES: SeriesDef[] = [
  { id: 'lavinia', name: 'Lavinia' },
  { id: 'platea-pro', name: 'Platea Pro' },
  { id: 'street', name: 'Street' },
  { id: 'billboard', name: 'Billboard' },
];

// Optic library — Type I/II/III/IV/V are IES-standard street lighting distributions.
const OPTICS = ['Type II', 'Type II-M', 'Type III', 'Type III-S', 'Type IV', 'Type V', 'Asymmetric', 'Symmetric'];

interface VariantSeed {
  /** Variant code (also acts as SKU). */
  v: string;
  /** Power in watts. Generated within a realistic range per product. */
  w?: number;
  /** Color temperature in K. */
  k?: 3000 | 3500 | 4000 | 5000;
  /** Optic / distribution code. */
  optic?: string;
  segment: Segment;
}

interface ProductDef {
  /** Human product label, e.g. "Pole mounted 617x333mm" */
  name: string;
  /** Typical wattage range for the housing size [min, max]. */
  wattRange: [number, number];
  variants: VariantSeed[];
}

interface SeriesCatalog {
  series: string; // e.g. "Lavinia"
  seriesId: string; // e.g. "lavinia"
  products: ProductDef[];
}

const CATALOG: SeriesCatalog[] = [
  // ── Lavinia ───────────────────────────────────────────────────────────────
  {
    series: 'Lavinia',
    seriesId: 'lavinia',
    products: [
      {
        name: 'Pole mounted 617x333mm',
        wattRange: [60, 200],
        variants: [
          { v: 'EH27', w: 80, k: 4000, optic: 'Type II', segment: 'flagship' },
          { v: 'EH23', w: 100, k: 4000, optic: 'Type III', segment: 'flagship' },
          { v: 'EH26', w: 120, k: 4000, optic: 'Type II-M', segment: 'workhorse' },
          { v: 'EH24', w: 150, k: 4000, optic: 'Type III', segment: 'workhorse' },
          { v: 'EH29', w: 180, k: 3000, optic: 'Type IV', segment: 'niche' },
          { v: 'EH31', w: 200, k: 4000, optic: 'Type V', segment: 'workhorse' },
          { v: 'EH32', w: 90, k: 3500, optic: 'Type II', segment: 'new' },
        ],
      },
    ],
  },
  // ── Platea Pro ────────────────────────────────────────────────────────────
  {
    series: 'Platea Pro',
    seriesId: 'platea-pro',
    products: [
      {
        name: 'Pole mounted 406x276mm',
        wattRange: [40, 150],
        variants: [
          { v: 'P883', w: 60, k: 4000, optic: 'Type II', segment: 'flagship' },
          { v: 'P891', w: 80, k: 4000, optic: 'Type III', segment: 'flagship' },
          { v: 'P882', w: 100, k: 3000, optic: 'Type II-M', segment: 'workhorse' },
          { v: 'P890', w: 120, k: 4000, optic: 'Type III-S', segment: 'workhorse' },
          { v: 'P878', w: 50, k: 3500, optic: 'Asymmetric', segment: 'niche' },
          { v: 'P886', w: 140, k: 4000, optic: 'Type IV', segment: 'new' },
        ],
      },
    ],
  },
  // ── Street ────────────────────────────────────────────────────────────────
  {
    series: 'Street',
    seriesId: 'street',
    products: [
      {
        name: 'Pole-top 416x211mm',
        wattRange: [25, 80],
        variants: [
          { v: '2137', w: 30, k: 4000, optic: 'Type II', segment: 'workhorse' },
          { v: '2138', w: 40, k: 4000, optic: 'Type II', segment: 'flagship' },
          { v: '2139', w: 50, k: 3000, optic: 'Type III', segment: 'workhorse' },
          { v: '2132', w: 25, k: 3000, optic: 'Type I', segment: 'underperformer' },
          { v: '2134', w: 35, k: 3000, optic: 'Type II', segment: 'underperformer' },
          { v: '2143', w: 60, k: 4000, optic: 'Type II-M', segment: 'workhorse' },
          { v: '2148', w: 70, k: 4000, optic: 'Type III', segment: 'workhorse' },
          { v: '2145', w: 55, k: 4000, optic: 'Type II', segment: 'workhorse' },
          { v: '2149', w: 80, k: 5000, optic: 'Type III-S', segment: 'niche' },
          { v: '2146', w: 65, k: 3500, optic: 'Type II-M', segment: 'new' },
        ],
      },
      {
        name: 'Pole-top 529x268mm',
        wattRange: [60, 150],
        variants: [
          { v: 'UH47', w: 70, k: 4000, optic: 'Type II', segment: 'flagship' },
          { v: 'UH48', w: 80, k: 4000, optic: 'Type III', segment: 'flagship' },
          { v: 'EQ60', w: 90, k: 4000, optic: 'Type II-M', segment: 'workhorse' },
          { v: 'EQ55', w: 100, k: 4000, optic: 'Type III', segment: 'workhorse' },
          { v: 'EQ56', w: 110, k: 3000, optic: 'Type III-S', segment: 'workhorse' },
          { v: 'UH39', w: 60, k: 3000, optic: 'Type II', segment: 'underperformer' },
          { v: 'EQ27', w: 120, k: 4000, optic: 'Type IV', segment: 'niche' },
          { v: 'EQ34', w: 130, k: 4000, optic: 'Type IV', segment: 'workhorse' },
          { v: 'EQ32', w: 140, k: 5000, optic: 'Type V', segment: 'workhorse' },
          { v: 'EQ31', w: 150, k: 4000, optic: 'Type V', segment: 'workhorse' },
          { v: 'S752', w: 75, k: 3000, optic: 'Type II', segment: 'underperformer' },
          { v: 'S751', w: 85, k: 3000, optic: 'Type II-M', segment: 'underperformer' },
          { v: 'UH46', w: 95, k: 4000, optic: 'Type III', segment: 'workhorse' },
          { v: 'EQ66', w: 105, k: 4000, optic: 'Type III-S', segment: 'new' },
        ],
      },
      {
        name: 'Pole-top 650x328mm',
        wattRange: [100, 250],
        variants: [
          { v: 'EW12', w: 150, k: 4000, optic: 'Type IV', segment: 'flagship' },
          { v: 'S766', w: 200, k: 4000, optic: 'Type V', segment: 'workhorse' },
          { v: 'S765', w: 250, k: 5000, optic: 'Type V', segment: 'niche' },
        ],
      },
    ],
  },
  // ── Billboard ─────────────────────────────────────────────────────────────
  {
    series: 'Billboard',
    seriesId: 'billboard',
    products: [
      {
        name: 'Pole-top 450x209mm',
        wattRange: [80, 200],
        variants: [
          { v: 'BB-A11', w: 80, k: 4000, optic: 'Asymmetric', segment: 'workhorse' },
          { v: 'BB-A14', w: 120, k: 4000, optic: 'Asymmetric', segment: 'flagship' },
          { v: 'BB-A18', w: 180, k: 5000, optic: 'Asymmetric', segment: 'niche' },
        ],
      },
      {
        name: 'Pole-top 516x259mm',
        wattRange: [100, 300],
        variants: [
          { v: 'BB-B22', w: 150, k: 4000, optic: 'Asymmetric', segment: 'workhorse' },
          { v: 'BB-B25', w: 200, k: 4000, optic: 'Symmetric', segment: 'workhorse' },
          { v: 'BB-B28', w: 250, k: 5000, optic: 'Symmetric', segment: 'new' },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Flatten into MockLuminaire[].
// ─────────────────────────────────────────────────────────────────────────────

const buildAll = (): MockLuminaire[] => {
  const out: MockLuminaire[] = [];
  let idx = 0;
  for (const series of CATALOG) {
    for (const product of series.products) {
      for (const variant of product.variants) {
        const sku = variant.v;
        const rng = mulberry32(hashString(sku + ':spec'));
        const power = variant.w ?? Math.round(between(rng, ...product.wattRange));
        const cct = variant.k ?? (rng() > 0.5 ? 4000 : 3000);
        const optic = variant.optic ?? OPTICS[Math.floor(rng() * OPTICS.length)];
        // Efficacy varies by segment — flagships push lm/W harder.
        const efficacy =
          variant.segment === 'flagship'
            ? between(rng, 135, 165)
            : variant.segment === 'underperformer'
              ? between(rng, 85, 110)
              : variant.segment === 'new'
                ? between(rng, 125, 150)
                : between(rng, 105, 135);
        const lumenOutput = Math.round(power * efficacy);
        const cri =
          variant.segment === 'flagship'
            ? intBetween(rng, 80, 95)
            : variant.segment === 'underperformer'
              ? intBetween(rng, 65, 75)
              : intBetween(rng, 70, 88);

        out.push({
          id: `lum_${idx}_${sku.toLowerCase()}`,
          sku,
          name: `${series.series} ${sku}`,
          manufacturer: 'IGUZZINI',
          seriesId: series.seriesId,
          series: series.series,
          product: product.name,
          opticCode: optic,
          power,
          lumenOutput,
          colorTemperature: cct,
          cri,
          ipRating: rng() > 0.5 ? 'IP66' : 'IP65',
          thumbnailColor: SERIES_THUMB_COLOR[series.seriesId] ?? '#4f46e5',
          imageUrl: SERIES_IMAGE[series.seriesId] ?? '',
          iesUrl: IES_FILE[sku] ? `/lamps/ies/${IES_FILE[sku]}` : undefined,
          segment: variant.segment,
          analytics: buildAnalytics(sku, variant.segment),
        });
        idx += 1;
      }
    }
  }
  return out;
};

export const MOCK_LUMINAIRES: MockLuminaire[] = buildAll();
